import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Search,
  Moon,
  Sun,
  Menu,
  ChevronDown,
  X,
  LogOut,
  Clock,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CareGiverContext } from "../context/CareGiverContext";
import { useTheme } from "../context/ThemeContext";
import { assets } from "../assets/assets";
import LogoutModal from "./LogoutModal";

const Navbar = ({ onMenuClick }) => {
  const { t, i18n } = useTranslation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentPages, setRecentPages] = useState([]);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const language = i18n.language ? i18n.language.substring(0, 2) : "en";
  const searchInputRef = useRef(null);
  const langDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  const navigate = useNavigate();
  const { setCToken, profileData } = useContext(CareGiverContext);
  const { isDarkMode, toggleDarkMode } = useTheme();

  const languageOptions = [
    { code: "en", name: "English", short: "EN", flag: assets.ukFlag },
    { code: "am", name: "አማርኛ", short: "አማ", flag: assets.ethiopiaFlag },
    {
      code: "om",
      name: "Afaan Oromoo",
      short: "OM",
      flag: assets.ethiopiaFlag,
    },
    { code: "tg", name: "ትግርኛ", short: "ትግ", flag: assets.ethiopiaFlag },
  ];

  const handleLanguageSelect = (lang) => {
    i18n.changeLanguage(lang.code);
    setLangDropdownOpen(false);
  };

  const quickActions = [
    {
      title: "Toggle Dark Mode",
      action: () => {
        toggleDarkMode();
        setShowSearch(false);
      },
      category: "Quick Action",
      icon: isDarkMode ? Sun : Moon,
    },
    {
      title: "Logout",
      action: () => {
        setShowSearch(false);
        setShowLogoutModal(true);
      },
      category: "Quick Action",
      icon: LogOut,
    },
  ];

  const caregiverSearchItems = [
    {
      title: "Dashboard",
      path: "/caregiver/dashboard",
      category: "Navigation",
    },
    {
      title: "Bookings",
      path: "/caregiver/bookings",
      category: "Navigation",
    },
    {
      title: "Service Requests",
      path: "/caregiver/request/apply",
      category: "Navigation",
    },
    {
      title: "Application Status",
      path: "/caregiver/application-status",
      category: "Navigation",
    },
    {
      title: "Contracts",
      path: "/caregiver/contracts",
      category: "Navigation",
    },
    {
      title: "Payments",
      path: "/caregiver/payments",
      category: "Navigation",
    },
    {
      title: "Profile",
      path: "/caregiver/profile",
      category: "Navigation",
    },
    {
      title: "Help",
      path: "/caregiver/help",
      category: "Navigation",
    },
  ];

  const searchItems = caregiverSearchItems;

  useEffect(() => {
    const stored = localStorage.getItem("caregiverRecentPages");
    if (stored) {
      setRecentPages(JSON.parse(stored));
    }
  }, []);

  const saveRecentPage = (item) => {
    const updated = [
      item,
      ...recentPages.filter((p) => p.path !== item.path),
    ].slice(0, 3);
    setRecentPages(updated);
    localStorage.setItem("caregiverRecentPages", JSON.stringify(updated));
  };

  const filteredResults = searchQuery.trim()
    ? [
        ...searchItems.filter((item) =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
        ...quickActions.filter((action) =>
          action.title.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      ]
    : [];

  const userName = profileData?.name || "Caregiver";
  const profileImage = profileData?.image || "https://via.placeholder.com/150";

  const getDisplayName = () => {
    if (userName) {
      const nameParts = userName.split(" ");
      if (nameParts.length > 0) {
        return nameParts[0];
      }
      return userName;
    }
    return "C";
  };

  const getInitials = () => {
    if (userName) {
      const nameParts = userName.split(" ");
      if (nameParts.length > 1) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }
      return userName[0].toUpperCase();
    }
    return "C";
  };

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    setCToken(null);
    navigate("/login");
  };

  const handleSearchItemClick = (item) => {
    if (item.action) {
      item.action();
    } else if (item.path) {
      saveRecentPage(item);
      navigate(item.path);
      setShowSearch(false);
    }
    setSearchQuery("");
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch(true);
      }
      if (e.key === "Escape" && showSearch) {
        setShowSearch(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showSearch]);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSearch && !e.target.closest(".search-modal")) {
        setShowSearch(false);
        setSearchQuery("");
      }
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(e.target)
      ) {
        setLangDropdownOpen(false);
      }
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(e.target)
      ) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSearch]);

  return (
    <>
      <header
        className={`h-20 flex items-center justify-between px-4 md:px-8 border-b z-10 ${
          isDarkMode
            ? "bg-dark-surface border-dark-border"
            : "bg-card border-accent"
        }`}
      >
        {/* Left */}
        <div className="flex items-center gap-4 flex-1">
          <button
            onClick={onMenuClick}
            className={`md:hidden p-2 rounded-lg ${
              isDarkMode
                ? "text-dark-muted hover:bg-dark-background"
                : "text-text hover:bg-background"
            }`}
          >
            <Menu size={24} />
          </button>

          {/* Mobile Search Button */}
          <button
            onClick={() => setShowSearch(true)}
            className={`md:hidden p-2 rounded-lg ${
              isDarkMode
                ? "text-dark-muted hover:bg-dark-background"
                : "text-text hover:bg-background"
            }`}
          >
            <Search size={20} />
          </button>

          <div
            onClick={() => setShowSearch(true)}
            className={`hidden md:flex items-center px-4 py-2.5 rounded-full border w-full max-w-md cursor-pointer transition-colors ${
              isDarkMode
                ? "bg-dark-background border-dark-border hover:border-primary/50"
                : "bg-white border-accent hover:border-primary/50"
            }`}
          >
            <Search
              size={20}
              className={isDarkMode ? "text-dark-muted" : "text-gray-400"}
            />
            <span
              className={`ml-3 text-sm ${
                isDarkMode ? "text-dark-muted" : "text-gray-400"
              }`}
            >
              {t("navbar.searchPlaceholder")}
            </span>
            <kbd
              className={`ml-auto px-2 py-0.5 text-xs rounded border ${
                isDarkMode
                  ? "bg-dark-surface border-dark-border text-dark-muted"
                  : "bg-gray-100 border-gray-300 text-gray-500"
              }`}
            >
              Ctrl+K
            </kbd>
          </div>
        </div>

        {/* Right */}
        <div className="flex items-center gap-4">
          {/* Welcome Message */}
          <div
            className={`hidden md:block ${
              isDarkMode ? "text-dark-muted" : "text-gray-600"
            }`}
          >
            {t("navbar.welcome")},{" "}
            <span className="font-semibold">{getDisplayName()}</span>
          </div>

          {/* Language Dropdown */}
          <div className="relative" ref={langDropdownRef}>
            <button
              onClick={() => setLangDropdownOpen(!langDropdownOpen)}
              className={`flex items-center gap-2 px-3 py-2 rounded-2xl transition border ${
                isDarkMode
                  ? "bg-dark-background border-dark-border hover:border-primary/50 text-dark-text"
                  : "bg-gray-100/80 border-gray-200/60 hover:bg-gray-200/80 text-text"
              }`}
            >
              <img
                src={language === "en" ? assets.ukFlag : assets.ethiopiaFlag}
                className="w-5 h-5 rounded-full object-cover ring-1 ring-gray-200 dark:ring-gray-600"
                alt=""
              />
              <span className="font-semibold text-xs">
                {languageOptions.find((l) => l.code === language)?.short ||
                  "EN"}
              </span>
              <ChevronDown
                size={14}
                className={`transition-transform duration-200 ${langDropdownOpen ? "rotate-180" : ""} ${isDarkMode ? "text-dark-muted" : "text-gray-400"}`}
              />
            </button>

            <div
              className={`absolute top-full right-0 mt-2 w-44 rounded-2xl shadow-xl border overflow-hidden z-50 transition-[opacity,transform] duration-200 ${
                langDropdownOpen
                  ? "opacity-100 translate-y-0 pointer-events-auto"
                  : "opacity-0 translate-y-1 pointer-events-none"
              } ${
                isDarkMode
                  ? "bg-dark-surface border-dark-border"
                  : "bg-card border-gray-100"
              }`}
            >
              {languageOptions.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                    language === lang.code
                      ? "bg-primary/10 text-primary"
                      : isDarkMode
                        ? "text-dark-text hover:bg-dark-background"
                        : "text-text hover:bg-gray-50"
                  }`}
                >
                  <img
                    src={lang.flag}
                    className="w-5 h-5 rounded-full object-cover ring-1 ring-gray-200 dark:ring-gray-600"
                    alt=""
                  />
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Theme Toggle - Sun/Moon animated */}
          <div
            onClick={toggleDarkMode}
            className={`
              relative w-16 h-8 rounded-full cursor-pointer transition-colors duration-500 overflow-hidden border border-black/5 dark:border-white/10
              ${isDarkMode ? "bg-[#1a202c]" : "bg-[#5CB8FF]"}
            `}
            title="Toggle Theme"
          >
            {/* Background Decorations */}
            {!isDarkMode ? (
              <>
                {/* Clouds */}
                <div className="absolute top-1 right-2 w-4 h-4 rounded-full bg-white opacity-40 blur-[1px]"></div>
                <div className="absolute bottom-[-4px] right-4 w-6 h-6 rounded-full bg-white opacity-50 blur-[1px]"></div>
                <div className="absolute top-2 right-6 w-3 h-3 rounded-full bg-white opacity-30 blur-[1px]"></div>
              </>
            ) : (
              <>
                {/* Stars */}
                <div className="absolute top-2 right-4 w-[2px] h-[2px] bg-white rounded-full animate-pulse"></div>
                <div className="absolute bottom-2 right-2 w-[1px] h-[1px] bg-white rounded-full"></div>
                <div className="absolute top-3 left-8 w-[2px] h-[2px] bg-white rounded-full opacity-70"></div>
                <div className="absolute bottom-1 left-7 w-[1px] h-[1px] bg-white rounded-full opacity-50"></div>
              </>
            )}

            {/* The Switch Knob (Sun/Moon) */}
            <div
              className={`
                absolute top-[3px] w-[24px] h-[24px] rounded-full shadow-md z-10 flex items-center justify-center
                transition-[left,background-color] duration-300 ease-[cubic-bezier(0.68,-0.55,0.265,1.55)]
                ${
                  !isDarkMode
                    ? "left-[4px] bg-[#FFD700]" // Sun
                    : "left-[calc(100%-28px)] bg-[#E2E8F0]" // Moon
                }
              `}
            >
              {isDarkMode && (
                <div className="relative w-full h-full">
                  {/* Moon Craters */}
                  <div className="absolute top-[5px] right-[6px] w-[5px] h-[5px] bg-gray-400/50 rounded-full"></div>
                  <div className="absolute bottom-[4px] left-[6px] w-[6px] h-[6px] bg-gray-400/50 rounded-full"></div>
                  <div className="absolute top-[10px] left-[4px] w-[3px] h-[3px] bg-gray-400/50 rounded-full"></div>
                </div>
              )}
            </div>
          </div>

          {/* Profile */}
          <div className="relative" ref={profileDropdownRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className={`flex items-center gap-3 p-1.5 rounded-full ${
                isDarkMode ? "hover:bg-dark-background" : "hover:bg-background"
              }`}
            >
              {profileImage &&
              profileImage !== "https://via.placeholder.com/150" ? (
                <img
                  src={profileImage}
                  alt={userName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    isDarkMode
                      ? "bg-dark-background text-dark-text"
                      : "bg-accent text-secondary"
                  }`}
                >
                  {getInitials()}
                </div>
              )}
              <ChevronDown
                size={16}
                className={isDarkMode ? "text-dark-muted" : "text-gray-400"}
              />
            </button>

            {showProfileMenu && (
              <div
                className={`absolute right-0 mt-2 w-56 rounded-xl shadow-xl border ${
                  isDarkMode
                    ? "bg-dark-surface border-dark-border"
                    : "bg-white border-accent"
                }`}
              >
                <div
                  className={`px-4 py-3 border-b ${
                    isDarkMode ? "border-dark-border" : "border-accent"
                  }`}
                >
                  <p
                    className={`font-medium ${
                      isDarkMode ? "text-dark-text" : "text-text"
                    }`}
                  >
                    {userName}
                  </p>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-dark-muted" : "text-gray-500"
                    }`}
                  >
                    Caregiver
                  </p>
                </div>

                <Link
                  to="/caregiver/profile"
                  className={`block px-4 py-3 text-sm ${
                    isDarkMode
                      ? "text-dark-text hover:bg-dark-background"
                      : "hover:bg-accent"
                  }`}
                  onClick={() => setShowProfileMenu(false)}
                >
                  {t("navbar.profile")}
                </Link>
                <div
                  className={`px-4 py-3 border-t ${
                    isDarkMode ? "border-dark-border" : "border-accent"
                  }`}
                >
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      setShowLogoutModal(true);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm rounded-lg ${
                      isDarkMode
                        ? "text-red-400 hover:bg-red-400 hover:text-white"
                        : "text-red-500 hover:bg-red-50"
                    } transition-colors`}
                  >
                    {t("navbar.logout")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
        isDarkMode={isDarkMode}
      />

      {/* Search Modal */}
      {showSearch && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-black/50 backdrop-blur-sm">
          <div
            className={`search-modal w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 ${
              isDarkMode ? "bg-dark-surface" : "bg-white"
            }`}
          >
            <div
              className={`flex items-center gap-3 px-4 py-4 border-b ${
                isDarkMode ? "border-dark-border" : "border-gray-200"
              }`}
            >
              <Search
                size={20}
                className={isDarkMode ? "text-dark-muted" : "text-gray-400"}
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("navbar.searchModalPlaceholder")}
                className={`flex-1 bg-transparent outline-none text-base ${
                  isDarkMode
                    ? "text-dark-text placeholder-dark-muted"
                    : "text-text placeholder-gray-400"
                }`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className={`p-1 rounded-lg transition-colors ${
                    isDarkMode
                      ? "hover:bg-dark-background text-dark-muted"
                      : "hover:bg-gray-100 text-gray-400"
                  }`}
                  title="Clear search"
                >
                  <X size={16} />
                </button>
              )}
              <button
                onClick={() => {
                  setShowSearch(false);
                  setSearchQuery("");
                }}
                className={`p-1.5 rounded-lg transition-colors ${
                  isDarkMode
                    ? "hover:bg-dark-background text-dark-muted hover:text-dark-text"
                    : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                }`}
                title="Close search (ESC)"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {searchQuery.trim() === "" ? (
                <div className="py-2">
                  {recentPages.length > 0 && (
                    <>
                      <div
                        className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${
                          isDarkMode ? "text-dark-muted" : "text-gray-400"
                        }`}
                      >
                        {t("navbar.recentPages")}
                      </div>
                      {recentPages.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => handleSearchItemClick(item)}
                          className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                            isDarkMode
                              ? "hover:bg-dark-background text-dark-text"
                              : "hover:bg-gray-50 text-text"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                isDarkMode
                                  ? "bg-dark-background"
                                  : "bg-gray-100"
                              }`}
                            >
                              <Clock size={16} className="text-primary" />
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-sm">
                                {item.title}
                              </p>
                              <p
                                className={`text-xs ${
                                  isDarkMode
                                    ? "text-dark-muted"
                                    : "text-gray-500"
                                }`}
                              >
                                {item.category}
                              </p>
                            </div>
                          </div>
                          <ChevronDown
                            size={16}
                            className={`-rotate-90 ${
                              isDarkMode ? "text-dark-muted" : "text-gray-400"
                            }`}
                          />
                        </button>
                      ))}
                    </>
                  )}

                  <div
                    className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? "text-dark-muted" : "text-gray-400"
                    } ${recentPages.length > 0 ? "mt-2" : ""}`}
                  >
                    {t("navbar.quickActions")}
                  </div>
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearchItemClick(action)}
                      className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                        isDarkMode
                          ? "hover:bg-dark-background text-dark-text"
                          : "hover:bg-gray-50 text-text"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isDarkMode ? "bg-dark-background" : "bg-gray-100"
                          }`}
                        >
                          {React.createElement(action.icon, {
                            size: 16,
                            className: "text-primary",
                          })}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-sm">{action.title}</p>
                          <p
                            className={`text-xs ${
                              isDarkMode ? "text-dark-muted" : "text-gray-500"
                            }`}
                          >
                            {action.category}
                          </p>
                        </div>
                      </div>
                      <ChevronDown
                        size={16}
                        className={`-rotate-90 ${
                          isDarkMode ? "text-dark-muted" : "text-gray-400"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              ) : filteredResults.length > 0 ? (
                <div className="py-2">
                  {filteredResults.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearchItemClick(item)}
                      className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                        isDarkMode
                          ? "hover:bg-dark-background text-dark-text"
                          : "hover:bg-gray-50 text-text"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isDarkMode ? "bg-dark-background" : "bg-gray-100"
                          }`}
                        >
                          {item.icon ? (
                            React.createElement(item.icon, {
                              size: 16,
                              className: "text-primary",
                            })
                          ) : (
                            <Search size={16} className="text-primary" />
                          )}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-sm">{item.title}</p>
                          <p
                            className={`text-xs ${
                              isDarkMode ? "text-dark-muted" : "text-gray-500"
                            }`}
                          >
                            {item.category}
                          </p>
                        </div>
                      </div>
                      <ChevronDown
                        size={16}
                        className={`-rotate-90 ${
                          isDarkMode ? "text-dark-muted" : "text-gray-400"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-8 text-center">
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-dark-muted" : "text-gray-500"
                    }`}
                  >
                    {t("navbar.noResults")} "{searchQuery}"
                  </p>
                </div>
              )}
            </div>

            <div
              className={`px-4 py-3 border-t flex items-center justify-between text-xs ${
                isDarkMode
                  ? "border-dark-border text-dark-muted"
                  : "border-gray-200 text-gray-500"
              }`}
            >
              <div className="flex items-center gap-4">
                <span>{t("navbar.navigateInfo")}</span>
                <span>{t("navbar.selectInfo")}</span>
              </div>
              <span>{t("navbar.closeInfo")}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
